import { useCallback, useEffect, useRef, useState } from "react";

import type { GroupId } from "../workspace/workspace.types";
import { useGroupRegistry } from "./GroupRegistry.context";

export const GROUP_SYMBOL_UPDATE_ERROR =
  "Unable to update group symbol. Try again.";

export const useGroupSymbolMutation = (groupId?: GroupId | null) => {
  const registry = useGroupRegistry();
  const [groupSymbolError, setGroupSymbolError] = useState<string>();
  const mountedRef = useRef(true);
  const requestRef = useRef(0);
  const seenGroupIdRef = useRef(groupId);

  // Group assignment does not go through setGroupSymbol. Drop a leftover
  // error and any in-flight reject so they cannot describe a former group.
  if (seenGroupIdRef.current !== groupId) {
    seenGroupIdRef.current = groupId;
    requestRef.current += 1;
    setGroupSymbolError(undefined);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  const setGroupSymbol = useCallback(
    (groupId: GroupId, symbol?: string) => {
      const request = requestRef.current + 1;
      requestRef.current = request;
      setGroupSymbolError(undefined);
      void registry.setGroupSymbol(groupId, symbol).catch(() => {
        if (mountedRef.current && requestRef.current === request) {
          setGroupSymbolError(GROUP_SYMBOL_UPDATE_ERROR);
        }
      });
    },
    [registry],
  );

  return { setGroupSymbol, groupSymbolError };
};
